import { Button, ButtonIcon } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import DotsNavigation from "@/atoms-react/dots-navigation/DotsNavigation.react";
import { Dropdown, type DropdownItem } from "@/atoms-react/dropdown/index.react";
import Flag from "@/atoms-react/flag/Flag.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react";
import type { LayoutBaseProps } from "@/atoms-react/layout-base/LayoutBase.react";
import { Base } from "@/atoms-react/text/Text.react";
import type { DeclarativePerson } from "@/features/flow/types";
import { useUser } from "@/features/user/hooks/use-user";
import type { NaturalPerson } from "@/features/user/types.ts";
import { FAVOURITE_COUNTRY_CODES } from "@/features/utils/constants";
import { useLocale } from "@/features/utils/hooks/use-locale";
import { FlowHeader } from "@/flow/components/flow-header.react";
import { useAppDispatch } from "@/redux/hooks";
import { setIsInPreviousStep } from "@/redux/slices/flowSlice";
import { setDeclarativeIdentityPersonNationalityCodes } from "@/redux/slices/signupSlice";
import { Themes } from "@/types/theme";
import { ChevronRightIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface DeclarativeIdentityOneProps extends LayoutBaseProps {
  onValidated?: (output: DeclarativeNationalityOutput) => void;
  profile?: NaturalPerson | null;
  signupStateNationalities?: string[];
}

export type DeclarativeNationalityOutput = DeclarativePerson;

function DeclarativeNationality({
  profile,
  onValidated = () => {},
  signupStateNationalities
}: DeclarativeIdentityOneProps) {
  const { locale } = useLocale();
  const { useCountries } = useUser();
  const { data: countries } = useCountries(locale.language);
  const { t } = useTranslation(["pass"]);
  const [isCheckboxSelected, setIsCheckboxSelected] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const getMyCountry = (id: number) => {
    return (countries?.countries ?? []).find((country) => country.id == id)?.alpha2;
  };
  const nationalityCodesInit = (): string[] => {
    if (
      signupStateNationalities &&
      signupStateNationalities.length > 0 &&
      signupStateNationalities[0] !== ""
    ) {
      return signupStateNationalities;
    }
    const nationalityCodes: string[] = [];
    nationalityCodes.push(getMyCountry(profile?.CountryOfCitizenship ?? -1) ?? "");
    nationalityCodes.push(getMyCountry(profile?.CountryOfCitizenship2 ?? -1) ?? "");
    nationalityCodes.push(getMyCountry(profile?.CountryOfCitizenship3 ?? -1) ?? "");
    nationalityCodes.push(getMyCountry(profile?.CountryOfCitizenship4 ?? -1) ?? "");

    for (let i = nationalityCodes.length; i > 0; i--) {
      if (nationalityCodes[i] === "") nationalityCodes.pop();
    }
    return nationalityCodes;
  };

  const [data, setDeclarativeIdentityOne] = useState<DeclarativePerson>({
    nationalityCodes: nationalityCodesInit()
  });

  const values = {
    nationalityCodes: data.nationalityCodes.map((nationalityCode) => ({ nationalityCode }))
  };

  const { control } = useForm({
    values
  });
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "nationalityCodes",
    rules: { maxLength: 4 }
  });

  const fieldNationalityCodes = fields.map((field) => field.nationalityCode);

  const dataWithUpdatedNationalityCodes = {
    ...data,
    nationalityCodes: fieldNationalityCodes
  };

  const isDuplicatedCountry =
    fieldNationalityCodes.filter((item, index) =>
      fieldNationalityCodes.some((elem, idx) => elem === item && idx !== index)
    ).length > 0;

  const handleDropdownAutoComplete = (
    autoComplete: string,
    items: DropdownItem[],
    change: (item: DropdownItem) => void
  ) => {
    const country = countries?.countries.find(
      (country) => country.alpha2.toLowerCase() === autoComplete.toLowerCase()
    );
    const autocompletedValue = items.find((item) => item?.key === country?.id.toString()) ?? null;

    if (autocompletedValue) change(autocompletedValue);
  };

  function execute() {
    const nationalityCodes = fields.map((field) => field.nationalityCode);
    setDeclarativeIdentityOne({ ...data, nationalityCodes });
    if (fields[0].nationalityCode === "" && fields.length === 1) {
      toast.error(t("flow.id_control.error.must_enter_fields"));
      return;
    }

    if (isDuplicatedCountry) {
      toast.error(t("flow.id_control.input.nationality_declarative_add_error"));
      return;
    }
    dispatch(setIsInPreviousStep(false));
    onValidated(dataWithUpdatedNationalityCodes);
  }

  const isAtLeastOneNationalitySelected = fields[fields.length - 1].nationalityCode !== "";
  const isMainCountryOfCitizenshipSelected = fields[0].nationalityCode !== "";

  const dropdownItems =
    countries?.countries.map((country) => ({
      key: country.alpha2,
      value: country.defaultName,
      icon: <Flag countryCode={country.alpha2} />,
      render: ({ key }: { key: string }) => (
        <div className={"flex w-full"} key={key}>
          <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
          <Base className={"ml-2"}>{country.defaultName}</Base>
        </div>
      ),
      groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
        ? "default"
        : undefined
    })) ?? [];

  const isSubmitDisabled =
    !isAtLeastOneNationalitySelected ||
    isCheckboxSelected === true ||
    !isMainCountryOfCitizenshipSelected;

  return (
    <Column className="justify-between">
      <div className="mb-12">
        <FlowHeader
          title={t("flow.id_control.input.Nationality_title")}
          back={true}
          onPreviousClick={() => {
            dispatch(setIsInPreviousStep(true));
            dispatch(setDeclarativeIdentityPersonNationalityCodes(fieldNationalityCodes));
          }}
        />
        {fields?.map((nationalityField, index) => (
          <div key={nationalityField.id} className="flex flex-row">
            <Dropdown
              required={true}
              label={index === 0 ? `${t("flow.id_control.input.nationality_label")}` : undefined}
              key={nationalityField.id}
              placeholder={t("flow.id_control.input.nationality_placeholder")}
              items={dropdownItems}
              value={nationalityField.nationalityCode}
              autoComplete="country"
              handleAutoComplete={handleDropdownAutoComplete}
              onChange={(value) => update(index, { nationalityCode: value.key })}
              groups={[{ key: "default" }]}
              search={true}
              className={twMerge("mt-4", "w-full")}
            />
            {index > 0 && fields[index].nationalityCode !== "" && (
              <div className="mt-2 flex flex-col justify-center">
                <ButtonIcon
                  size="md"
                  theme={Themes.white}
                  element={<TrashIcon />}
                  className="ml-2 cursor-pointer text-red-600"
                  onClick={() => {
                    remove(index);
                    setIsCheckboxSelected(false);
                  }}
                />
              </div>
            )}
          </div>
        ))}

        {fields.length < 4 && fields[fields.length - 1].nationalityCode !== "" && (
          <div className="mt-5 mb-5">
            <Base className="mt-4 text-base">
              {t("flow.id_control.input.add_nationality_checkbox")}
            </Base>
            <div className="mt-4 flex flex-row">
              <Checkbox
                value={isCheckboxSelected === true}
                onChange={() => setIsCheckboxSelected(true)}
              >
                <Base className="text-base">
                  {" "}
                  {t("flow.id_control.input.add_nationality_checkbox_yes")}
                </Base>
              </Checkbox>
              <Checkbox
                className="ml-4"
                value={isCheckboxSelected === false}
                onChange={() => setIsCheckboxSelected(false)}
              >
                <Base className="text-base">
                  {" "}
                  {t("flow.id_control.input.add_nationality_checkbox_no")}
                </Base>
              </Checkbox>
            </div>
          </div>
        )}

        {isCheckboxSelected === true && fields.length < 4 && isAtLeastOneNationalitySelected && (
          <div className="mt-4 flex flex-row">
            <Button
              size="md"
              theme={Themes.iconButton}
              icon={<PlusIcon />}
              onClick={() => {
                setIsCheckboxSelected(false);
                append({ nationalityCode: "" });
              }}
              className="rounded-sm bg-primary"
            />
            <Base className="ml-4">{t("flow.id_control.input.nationality_add")}</Base>
          </div>
        )}
      </div>
      {isDuplicatedCountry && (
        <Base className="mt-4 text-base text-error">
          {t("flow.id_control.input.nationality_declarative_add_error")}
        </Base>
      )}
      <div className="flex shrink-0 flex-col items-center">
        <DotsNavigation total={8} current={2} />
        <Button
          shortcut="enter"
          onClick={execute}
          icon={<ChevronRightIcon />}
          className="my-4"
          disabled={isSubmitDisabled}
        >
          {t("flow.id_control.input.continue")}
        </Button>
      </div>
    </Column>
  );
}

export default DeclarativeNationality;
